import React, { memo, useCallback } from "react";
import { cn } from "@racoon/ui";
import { TruckWithShifts } from "../model/types";
import { getVehicleStatusColor } from "../lib/utils";
import { DriverInfo } from "./driver-info";

interface VehicleCardProps {
  vehicle: TruckWithShifts;
  isSelected: boolean;
  onSelect: (vehicleId: string) => void;
  showDates?: boolean;
  selectedShiftId?: string;
  onShiftSelect?: (vehicleId: string, shiftId: string) => void;
}

const VehicleCardComponent: React.FC<VehicleCardProps> = ({
  vehicle,
  isSelected,
  onSelect,
  showDates,
  selectedShiftId,
  onShiftSelect,
}) => {
  const handleCardClick = useCallback(() => {
    onSelect(String(vehicle.id));
  }, [onSelect, vehicle.id]);

  const handleShiftSelect = useCallback(
    (shiftId: string) => {
      onShiftSelect?.(String(vehicle.id), shiftId);
    },
    [onShiftSelect, vehicle.id],
  );

  return (
    <div
      onClick={handleCardClick}
      role="button"
      tabIndex={0}
      className={cn(
        "p-4 rounded-lg cursor-pointer transition-all border",
        isSelected
          ? "border-brand-blue border-2"
          : "border-transparent hover:border-gray-300 hover:shadow-sm",
      )}
    >
      <div className="flex items-center gap-2 mb-4">
        <div className={cn("w-3 h-3 rounded-full", getVehicleStatusColor(vehicle))} />
        <div className="flex flex-1 flex-row justify-between">
          <span className="inline-flex items-center rounded-md text-sm border border-text-primary px-1.5 py-0.5 font-medium">
            {vehicle.plateNumber}
          </span>
          <span className="text-sm text-gray-700">{vehicle.model}</span>
        </div>
      </div>

      <div className="flex ml-5 flex-col justify-between mb-2" onClick={(e) => e.stopPropagation()}>
        <DriverInfo
          shifts={vehicle.shifts}
          cancelledShiftIds={vehicle.cancelledShiftIds}
          showDates={showDates}
          selectedShiftId={selectedShiftId}
          onShiftSelect={handleShiftSelect}
        />
      </div>
    </div>
  );
};

export const VehicleCard = memo(VehicleCardComponent);
